import { IconButton, Spinner } from "@chakra-ui/react-v3";
import { RiAddLine, RiCheckLine, RiErrorWarningLine } from "react-icons/ri";
import type { EhrWritePath } from "../../../../domain/ehr";
import { Tooltip } from "../../../ui/tooltip";
import { useEhrContext } from "../../shared-logic/ehr-context/ehr-context";
import type { TableRow } from "../utils/tables-metadata";
import { validateEhrWriteBack } from "./ehr-writeback-validator";
import { transformV2ToV1WriteBackFormat } from "./writeback-transformer";

type EhrActionButtonProps = {
  row: TableRow;
  path: EhrWritePath;
};

export function EhrActionButton({ row, path }: EhrActionButtonProps) {
  const ehr = useEhrContext();

  if (!ehr.isEhr || !ehr.ehrDetails) return null;

  const resourceId = row.id;

  const isWriting = ehr.writeBackStatus.writingIds.includes(resourceId);
  const isSuccess = ehr.writeBackStatus.successIds.includes(resourceId);
  const failedError = ehr.writeBackStatus.failedIds.find(r => r.id === resourceId);

  // Validate if this resource can be written to the EHR (similar to V1 logic copied)
  const validation = validateEhrWriteBack(row.originalData, path, ehr.currentEhr);
  const disabledReason = validation.isValid ? undefined : validation.reason;

  if (isWriting) {
    return (
      <Tooltip content="Processing" positioning={{ placement: "top" }}>
        <IconButton size="sm" variant="ghost" cursor="default" aria-label="Writing to EHR">
          <Spinner color="status.info" />
        </IconButton>
      </Tooltip>
    );
  }

  if (isSuccess) {
    return (
      <Tooltip content="Successfully written to EHR" positioning={{ placement: "top" }}>
        <IconButton
          cursor="default"
          size="sm"
          variant="ghost"
          color="status.success"
          aria-label="Success"
        >
          <RiCheckLine />
        </IconButton>
      </Tooltip>
    );
  }

  if (failedError) {
    return (
      <Tooltip
        content={`Something went wrong. Please try again later.`}
        positioning={{ placement: "top" }}
      >
        <IconButton size="sm" variant="ghost" color="status.error" aria-label="Retry">
          <RiErrorWarningLine />
        </IconButton>
      </Tooltip>
    );
  }

  if (disabledReason) {
    return (
      <Tooltip content={disabledReason} positioning={{ placement: "top" }}>
        <IconButton size="sm" variant="ghost" disabled aria-label="Cannot write">
          <RiAddLine />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={`Write to ${ehr.ehrDetails?.name}`} positioning={{ placement: "top" }}>
      <IconButton
        size="sm"
        variant="ghost"
        onClick={() => {
          // Transform V2 → V1 format for legacy EHR write-back compatibility
          const resource = transformV2ToV1WriteBackFormat(row.originalData, path);
          ehr.writeBackToEhr({ resourceId, resource, path });
        }}
        aria-label="Write to EHR"
      >
        <RiAddLine />
      </IconButton>
    </Tooltip>
  );
}
